<script>
import { getRichTextContentLength } from '@/support/utils/richTextHelper'

export default {
  name: 'RichTextEditor',
  components: {
    ValidationMessage: () => import('@/components/general/ValidationMessage')
  },

  props: {
    description: {
      type: String,
      default: null
    },

    maxLength: {
      type: Number,
      default: null
    },

    enforceMaxLength: {
      type: Boolean,
      default: true
    },

    options: {
      type: Object,
      default: () => ({
        modules: {
          clipboard: {
            matchVisual: false
          }
        }
      })
    },

    toolbar: {
      type: Array,
      default: () => [
        [
          {
            size: [
              'small',
              false,
              'large'
            ]
          }
        ],
        [
          {
            color: [
              '#707070',
              '#005EB8'
            ]
          }
        ],
        [
          'bold',
          'italic',
          'underline',
          'strike'
        ],
        [
          { align: '' },
          { align: 'center' },
          { align: 'right' },
          { align: 'justify' }
        ],
        [ 'clean' ]
      ]
    },

    value: null,
    validation: {
      type: Object,
      default: () => ({})
    },

    disabled: {
      type: Boolean,
      default: false
    },

    dark: {
      type: Boolean,
      default: false
    },

    underDescription: {
      type: [
        String,
        Array
      ],

      default: null
    }
  },

  data () {
    return {
      editorQuill: null,
      mutableValue: this.value
    }
  },

  computed: {
    isRequired () {
      return this.validation.$params && typeof this.validation.$params.required === 'object'
    },

    trueCharacterCounter () {
      if (this.editorQuill) {
        const text = this.editorQuill.getText().replace(/\n$/, '')

        return text.length
      }

      return getRichTextContentLength(this.mutableValue)
    },

    hasMaxLengthError () {
      return !!this.maxLength && this.trueCharacterCounter > this.maxLength
    },

    computedValidation () {
      if (this.validation.$error) {
        return this.validation
      }

      return {
        $error: this.hasMaxLengthError,
        maxLength: !this.hasMaxLengthError,
        $params: {
          maxLength: {
            max: this.maxLength
          }
        }
      }
    }
  },

  watch: {
    value: {
      handler (value) {
        this.mutableValue = this.cleanQlCursor(value)
      },

      immediate: true
    },

    mutableValue () {
      this.updateValue()
      this.$emit('input', this.mutableValue)
    }
  },

  mounted () {
    this.editorQuill = this.$refs.editor.quill
    this.editorQuill.root.addEventListener('keydown', this.handleKeyDown)
  },

  beforeDestroy () {
    if (this.editorQuill) {
      this.editorQuill.root.removeEventListener('keydown', this.handleKeyDown)
    }
  },

  methods: {
    updateValue (value = this.mutableValue) {
      typeof this.validation.$touch === 'function' && this.validation.$touch()
      this.$emit('input', value)
    },

    cleanQlCursor (value) {
      if (!value) return value

      return value.replaceAll(/<span class="ql-cursor">.*<\/span>/g, '')
    },

    getEditorTextLength () {
      if (!this.editorQuill) {
        return this.trueCharacterCounter
      }

      const text = this.editorQuill.getText().replace(/\n$/, '')

      return text.length
    },

    onEditorReady (quill) {
      this.editorQuill = quill
      this.$emit('ready', quill)
    },

    onSelectionChange (range, oldRange, source) {
      this.$emit('selection-change', range, oldRange, source)
    },

    handleTextChange (delta, oldDelta, source) {
      if (source === 'user' && this.maxLength && this.enforceMaxLength) {
        const textLength = this.getEditorTextLength()

        if (textLength > this.maxLength) {
          const excess = textLength - this.maxLength

          this.editorQuill.deleteText(this.maxLength, excess)
          this.mutableValue = this.editorQuill.root.innerHTML
        }
      }
    },

    handleKeyDown (e) {
      const allowedKeys = [
        'Backspace',
        'Tab',
        'Escape',
        'Shift',
        'Alt',
        'Control',
        'Meta',
        'CapsLock',
        'Home',
        'End',
        'PageUp',
        'PageDown',
        'ArrowLeft',
        'ArrowRight',
        'ArrowUp',
        'ArrowDown',
        'Delete'
      ]

      if (allowedKeys.includes(e.key) || e.ctrlKey || e.metaKey) {
        return
      }

      const selection = this.editorQuill && typeof this.editorQuill.getSelection === 'function'
        ? this.editorQuill.getSelection()
        : null

      if (selection && selection.length > 0) {
        return
      }

      if (this.maxLength && this.enforceMaxLength && this.getEditorTextLength() >= this.maxLength) {
        e.preventDefault()
      }
    }
  }
}
</script>

<template>
  <div
    class="form-item form-item-rich-text-box"
    :class="{ 'has-error': computedValidation.$error, 'theme-dark': dark, 'has-underdescription': underDescription }"
  >
    <p
      v-if="description"
      class="form-item-description"
    >
      {{ description }} <span v-if="!isRequired && !disabled">{{ $t('global:form.optional') }}</span>
    </p>
    <div
      v-if="underDescription && typeof underDescription === 'string'"
      class="form-input-subtext"
    >
      <p
        class="form-input-subtext"
        v-html="underDescription"
      />
    </div>
    <div
      v-if="underDescription && typeof underDescription === 'object'"
      class="form-input-subtext"
    >
      <p
        v-for="(paragraph, paragraphIndex) in underDescription"
        :key="paragraphIndex"
        class="form-input-subtext"
        v-html="paragraph"
      />
    </div>
    <VueEditor
      ref="editor"
      v-model="mutableValue"
      :disabled="disabled"
      :editor-toolbar="toolbar"
      :editor-options="options"
      @text-change="handleTextChange"
      @ready="onEditorReady"
      @selection-change="onSelectionChange"
    />
    <span
      v-if="maxLength"
      class="form-input-counter"
    >
      {{ trueCharacterCounter }} / {{ maxLength }}
    </span>
    <div
      v-if="computedValidation.$error"
      class="form-input-details"
    >
      <ValidationMessage :validation="computedValidation" />
    </div>
  </div>
</template>

<style src="@/assets/styles/themes/default/form.css"></style>

<style lang="scss">
.form-item-rich-text-box {
  .form-input-counter {
    position: relative;
    text-align: right;
    display: block;
    top: 4px;
    pointer-events: none;
  }

  &.has-error .form-input-counter {
    position: absolute;
    bottom: 0;
    top: auto;
  }
}
</style>
